/**
 * Centralized color tokens for the Onboard pack — single source of truth for
 * hex values previously duplicated across admin `styles.ts`, `Portal/styles.ts`,
 * and `SetupPortal` module consts.
 *
 * Brand colors (SCOUT_BLUE, SCOUT_TEAL, DARK_GREY, GREY, WHITE, RED, YELLOW) are
 * sourced from `@host/components/ThemeContext/constants` so the pack never
 * duplicates a hex the host already owns; only pack-specific colors (status
 * chips, borders, tints, non-brand accents) live here.
 *
 * Token values MUST NOT be edited without a visual review: each value is the
 * exact hex previously inlined at a call site, consolidated only to remove
 * duplication and casing drift (#084D6D was spelled three ways). Changing a
 * value is a visual change; swapping a literal for a same-value token is a pure
 * refactor. Inline comments record the original hex each token owns so future
 * edits can verify the mapping.
 */

import {
  BLACK,
  DARK_GREY,
  DARK_RED,
  GREEN,
  GREY,
  MEDIUM_RED,
  PLACEHOLDER,
  RED,
  SCOUT_BLUE,
  SCOUT_TEAL,
  SUCCESS_GREEN,
  WHITE,
  YELLOW
} from '@host/components/ThemeContext/constants';

const brand = {
  /** #084D6D — primary brand navy (was spelled #084D6D / #084d6d). Host: SCOUT_BLUE. */
  primary: SCOUT_BLUE,
  /** #5BC4BF — primary teal accent. Host: SCOUT_TEAL. */
  teal: SCOUT_TEAL,
  /** #418B88 — deeper teal for portal headings/links. Pack-specific (no host equivalent). */
  tealDeep: '#418B88',
  /** #033634 — darkest teal-green, contract template ink. Pack-specific (no host equivalent). */
  tealInk: '#033634',
  /** #4AADA8 — interactive teal for document drop zones. */
  tealInteractive: '#4AADA8'
} as const;

// Brand tints: light washes for hovers, active rows, soft fills.
const brandTint = {
  /** #DEF3F2 — light teal tint. */
  teal75: '#DEF3F2',
  /** #EFF9F9 — near-white teal wash. */
  teal50: '#EFF9F9',
  /** #E6EDF0 — light navy tint. */
  navy10: '#E6EDF0',
  /** #F0FAFA — active document drop-zone teal wash. */
  tealActive: '#F0FAFA'
} as const;

// Neutral text ramp: #333 (strong) -> #666 (body) -> #939 (muted). Short forms
// #333 / #666 are normalized to the 6-digit token.
const neutral = {
  /** #333333 — headings, strong body text, chip labels. */
  textStrong: '#333333',
  /** #5D5D5D — portal heading neutral. */
  textHeading: '#5D5D5D',
  /** #666666 — default body text. Normalizes #666. Host: DARK_GREY. */
  textBody: DARK_GREY,
  /** #939393 — muted text, outlined button labels. Host: GREY. */
  textMuted: GREY,
  /** #767676 — status-chip fallback / withdrawn. */
  textSubtle: '#767676',
  /** #6D6D6D — icon grey. */
  icon: '#6D6D6D',
  /** #1D2E3B — dark preview text. */
  textInk: '#1D2E3B',
  /** #555555 — secondary dark text. Normalizes #555. */
  textDark: '#555555',
  /** #656565 — setup portal footer text. */
  textFooter: '#656565',
  /** #737373 — quiet navigation and utility text. */
  textQuiet: '#737373',
  /** #838383 — form hint text. */
  textHint: '#838383',
  /** #858585 — dark placeholder text. */
  textPlaceholderDark: '#858585',
  /** #888888 — secondary muted text. Normalizes #888. */
  textMutedAlt: '#888888',
  /** #8D8D8D — inactive progress indicator. */
  progressInactive: '#8D8D8D',
  /** #989898 — secondary field and detail text. */
  textSecondary: '#989898',
  /** #999999 — subdued approval text. */
  textSubdued: '#999999',
  /** #9E9E9E — inactive status dot. */
  statusDotInactive: '#9E9E9E',
  /** #AAAAAA — disabled and archived text. Normalizes #aaa. */
  textDisabled: '#AAAAAA',
  /** #B9B9B9 — light placeholder text. */
  textPlaceholderLight: '#B9B9B9',
  /** #BBBBBB — disabled controls and placeholder marks. Normalizes #bbb. */
  disabled: '#BBBBBB'
} as const;

// Surfaces and backgrounds. `white` normalizes #FFFFFF / #fff.
const surface = {
  /** #FFFFFF — card / table / modal surfaces. Normalizes #fff. Host: WHITE. */
  white: WHITE,
  /** #FCFCFC — portal + setup portal page background. */
  page: '#FCFCFC',
  /** #F5F5F5 — neutral light fill. Normalizes #f5f5f5. */
  subtle: '#F5F5F5',
  /** #F3F3F3 — upload zone default background. */
  uploadZone: '#F3F3F3',
  /** #F0F0F0 — mid neutral fill. */
  fill: '#F0F0F0',
  /** #EEEEEE — muted chips, dividers, and history fills. */
  muted: '#EEEEEE',
  /** #F9F9F9 — document preview frame and top bar (matches the approval page). */
  previewFrame: '#F9F9F9',
  /** #D9D9D9 — document preview "Preview" tab. Host: PLACEHOLDER. */
  previewTab: PLACEHOLDER
} as const;

const border = {
  /** #DDDDDD — table row bottom border. */
  divider: '#DDDDDD',
  /** #EAEEF0 — icon-button / popover outline. */
  hairline: '#EAEEF0',
  /** #E0E0E0 — portal divider, status chip border. Normalizes #e0e0e0. */
  light: '#E0E0E0',
  /** #E3E3E3 — denser neutral border. */
  mid: '#E3E3E3',
  /** #CCCCCC — status clamp border, switch labels. Normalizes #ccc. */
  input: '#CCCCCC',
  /** #D0D0D0 — portal input border. */
  inputStrong: '#D0D0D0',
  /** #BBF7D0 — success chip border. */
  success: '#BBF7D0',
  /** #D8E1E7 — offer preview panel border. */
  preview: '#D8E1E7',
  /** #E8EEF2 — light offer and handover panel border. */
  panel: '#E8EEF2',
  /** #ECA8A6 — soft error field border. */
  errorSoft: '#ECA8A6',
  /** #FECACA — error chip border. */
  error: '#FECACA',
  /** #D5D9DA — timeline progress connector line (not-yet-reached segment). */
  stepperTrack: '#D5D9DA'
} as const;

// Status / semantic colors for status chips and offer-state indicators.
const status = {
  /** #2E7D32 — approved / completed green. */
  success: '#2E7D32',
  /** #7FBC88 — portal green, completed dot. */
  successLight: '#7FBC88',
  /** #C62828 — declined red. */
  error: '#C62828',
  /** #DE6763 — declined dot red. Host: RED. */
  errorSoft: RED,
  /** #E37D7A — warning red accent. */
  warningRed: '#E37D7A',
  /** #CF726F — secondary red. */
  warningRedDeep: '#CF726F',
  /** #E7B500 — caution yellow. */
  caution: '#E7B500',
  /** #996500 — caution yellow text. */
  cautionText: '#996500',
  /** #7B2FA0 — approval pending purple. */
  pending: '#7B2FA0',
  /** #1A56B0 — "new" / sent blue. */
  info: '#1A56B0',
  /** #6892FF — link / accent blue. */
  infoBright: '#6892FF',
  /** #0069C0 — setup status blue. */
  setup: '#0069C0',
  /** #2A8A85 — accepted offer teal. */
  offerAccepted: '#2A8A85',
  /** #429E4D — sent and active success green. Host: GREEN. */
  successBright: GREEN,
  /** #7CBD82 — completed portal progress green. */
  successPortal: '#7CBD82',
  /** #8CCF95 — soft smart-form success green. Host: SUCCESS_GREEN. */
  successSoft: SUCCESS_GREEN,
  /** #C9781D — warning helper text and icon. */
  warningText: '#C9781D',
  /** #E65100 — missing-field warning text. */
  missingText: '#E65100',
  /** #EC6E85 — portal medium-red accent. Host: MEDIUM_RED. */
  mediumRed: MEDIUM_RED,
  /** #D32F2F — PDF load-error text. Host: DARK_RED. */
  errorText: DARK_RED
} as const;

// Light washes paired with status colors for chip/row backgrounds.
const statusWash = {
  /** #FFF8E1 — caution wash. Normalizes #fff8e1. */
  caution: '#FFF8E1',
  /** #F0FDF4 — success wash. */
  success: '#F0FDF4',
  /** #FEF2F2 — error wash. */
  error: '#FEF2F2',
  /** #FFF6F5 — soft red wash. */
  errorSoft: '#FFF6F5',
  /** #E8F5E9 — configured-field success wash. */
  successMuted: '#E8F5E9',
  /** #EEF2FF — informational setup-stage wash. */
  info: '#EEF2FF',
  /** #FDF6EE — warning helper wash. */
  warningPale: '#FDF6EE',
  /** #FFF3E0 — missing-field warning wash. */
  warningStrong: '#FFF3E0'
} as const;

// Non-brand accents: orange/coral dots and badges.
const accent = {
  /** #F4845F — coral accent. */
  coral: '#F4845F',
  /** #E6A052 — pending-approval orange dot. Host: YELLOW (same hex, host names it YELLOW). */
  orange: YELLOW,
  /** #B25300 — negotiation / candidate-declined orange. */
  orangeDeep: '#B25300',
  /** #C66900 — attachments-requested orange. */
  orangeStatus: '#C66900',
  /** #B07D00 — "offer sent" amber. */
  amber: '#B07D00',
  /** #D1924B — in-progress tan dot. */
  tan: '#D1924B',
  /** #000000 — switch-track hover overlay. Host: BLACK. */
  switchTrackHover: BLACK,
  /** #5B6BB5 — setup-stage indigo text. */
  indigo: '#5B6BB5',
  /** #B080FF — pre-offer purple. */
  purple: '#B080FF'
} as const;

export const onboardColors = {
  brand,
  brandTint,
  neutral,
  surface,
  border,
  status,
  statusWash,
  accent
} as const;
